import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
  AcceptInvitationParams,
  AuthResponse,
  CreateBrandRepAccountParams,
  CreateBrandRepAccountResponse,
  CreateRoleResponse,
  ListBrandRepAccountsParams,
  ListBrandRepAccountsResponse,
  ListRolesParams,
  ListRolesResponse,
  ResendInvitationParams,
  ResendInvitationResponse,
  ResetPasswordRequestParams,
  ResetPasswordRequestResponse,
  ResetPasswordParams,
  DeleteRoleResponse,
  DeleteRoleParams,
  SetAccountPermissionsParams,
  SetAccountPermissionsResponse,
  GetAccountParams,
  GetAccountResponse,
  UpdateRoleParams,
  UpdateRolePermissionsResponse,
  CreateScopeParams,
  CreateScopeResponse,
  ListScopesParams,
  ListScopesResponse,
  UpdateScopeParams,
  UpdateScopeResponse,
  DeleteScopeParams,
  DeleteScopeResponse,
  SyncAccountsParams,
  SyncAccountsResponse,
  UpdateAccountStatusV2Response,
  UpdateAccountStatusV2Params,
  UpdateAccountV2Response,
  UpdateAccountV2Params,
} from '../dtos';

import { AdminAccount, Role } from '../interfaces';
import { ApiService } from 'src/app/core/services/api.service';
import { AppState } from 'src/app/core/reducers';
import {
  CookieService,
  JwtService,
  OxioAdminBrandService,
  PermissionService,
} from 'src/app/core/services';

import { CredentialsLogin } from 'src/app/core/interfaces';
import { AccountState } from 'src/app/core/store/account/account.reducer';
import { isOxioAdmin } from 'src/app/core/store/account/account.selectors';
import { purge } from 'src/app/core/store/account/account.actions';
import { actionBrandGet } from 'src/app/core/store/brands/brands.actions';
import { DefaultConst } from 'src/app/core/constants/default.constant';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AccountsService {
  isOxioAdmin = false;

  constructor(
    private readonly apiService: ApiService,
    private jwtService: JwtService,
    private permissionService: PermissionService,
    private oxioAdminBrandService: OxioAdminBrandService,
    private store: Store<AppState>,
    private cookieService: CookieService
  ) {
    this._isOxioAdmin();
  }

  /**
   * Get list of brand rep accounts
   * @param listBrandRepAccountsParams
   */
  listBrandRepAccounts(
    listBrandRepAccountsParams: ListBrandRepAccountsParams
  ): Observable<ListBrandRepAccountsResponse> {
    let params = new HttpParams();

    Object.keys(listBrandRepAccountsParams).forEach((param) => {
      params = params.append(param, listBrandRepAccountsParams[param]);
    });

    return this.apiService.get(`/v1/accounts/brand-reps/list`, params);
  }

  /**
   * Create brand rep acct
   * @param createBrandRepAccountParams
   * permissions: {mode: "+c", res: "accounts.*", dom: "[$brandUuid]", tokenDom: "[brand_uuid]"}
   */
  createBrandRepAccount(
    createBrandRepAccountParams: CreateBrandRepAccountParams
  ): Observable<CreateBrandRepAccountResponse> {
    return this.apiService.post(
      `/v1/accounts/brand-rep`,
      createBrandRepAccountParams
    );
  }

  /**
   * Resend user invitation
   * @param resendInvitationParams
   * permissions: {mode: "+r", res: "accounts.email"}
   * permissions: {mode: "+c", res: "invitationCode.*", dom: "brand.brandUuid.${auth.brandUuid}"}
   */
  resendInvitation(
    resendInvitationParams: ResendInvitationParams
  ): Observable<ResendInvitationResponse> {
    return this.apiService.post(
      `/v1/accounts/invitation/send`,
      resendInvitationParams
    );
  }

  /**
   * TODO: Remove function along with dtos files
   * Update user account status
   * @param updateAdminAccountStatusParams
   */
  // updateAdminAccountStatus(
  //   updateAdminAccountStatusParams: UpdateAdminAccountStatusParams
  // ): Observable<UpdateAdminAccountStatusResponse> {
  //   return this.apiService.post(
  //     `/v1/accounts/status/${updateAdminAccountStatusParams.entityType}/rep/${updateAdminAccountStatusParams.entityRepUuid}`,
  //     updateAdminAccountStatusParams
  //   );
  // }

  /**
   * Accept invitation
   * @param acceptInvitationParams
   */
  acceptInvitation(
    acceptInvitationParams: AcceptInvitationParams
  ): Observable<AuthResponse> {
    return this.apiService.post(
      '/v1/accounts/invitation',
      acceptInvitationParams
    );
  }

  /** Forgot Password
   * @method resetPasswordRequest
   * @param {ResetPasswordRequestParams} resetPasswordRequestParams
   * @returns {Observable<ResetPasswordRequestResponse>}
   */
  resetPasswordRequest(
    resetPasswordRequestParams: ResetPasswordRequestParams
  ): Observable<ResetPasswordRequestResponse> {
    return this.apiService.post(
      '/v1/accounts/creds/reset-password/request',
      resetPasswordRequestParams
    );
  }

  /** Reset Password
   * @method resetPassword
   * @param {ResetPasswordParams} resetPasswordRequestParams
   * @returns {Observable<AuthResponse>}
   */
  resetPassword(
    resetPasswordRequestParams: ResetPasswordParams
  ): Observable<AuthResponse> {
    return this.apiService.post(
      '/v1/accounts/creds/reset-password',
      resetPasswordRequestParams
    );
  }

  /**
   * // TODO: Unused function - Remove along with GetRolesFromPermissionsResponse interface?
   * Check if a role is a duplicate
   * @param role
   * permissions: {mode: "+r", res: "role.*"}
   */
  // getRolesFromPermissions(
  //   role: Role
  // ): Observable<GetRolesFromPermissionsResponse> {
  //   return this.apiService.post('/v1/accounts/permissions/roles', role);
  // }

  /**
   *
   * @param setAccountPermissionsParams
   * permissions: {mode: "+r", res: "resource.*"}
   */
  setAccountPermissions(
    setAccountPermissionsParams: SetAccountPermissionsParams
  ): Observable<SetAccountPermissionsResponse> {
    return this.apiService.post(
      `/v1/accounts/permissions/${setAccountPermissionsParams.entityType}/rep/${setAccountPermissionsParams.entityRepUuid}`,
      setAccountPermissionsParams
    );
  }

  dispatchBrand(
    isOxioAdmin: boolean,
    adminAccount: AdminAccount & GetAccountResponse
  ) {
    // get brand state if already registered
    const brandUuid = this.oxioAdminBrandService.getBrandUuid();

    if (isOxioAdmin && brandUuid) {
      this.store.dispatch(actionBrandGet({ brandUuid }));
    } else if (adminAccount.brandId) {
      this.store.dispatch(actionBrandGet({ brandUuid: adminAccount.brandId }));
    } else if (adminAccount.brandUuid) {
      this.store.dispatch(
        actionBrandGet({ brandUuid: adminAccount.brandUuid })
      );
    }
  }

  private _isOxioAdmin() {
    this.store
      .pipe(select(isOxioAdmin), untilDestroyed(this))
      .subscribe((isOxioAdmin: boolean) => {
        this.isOxioAdmin = isOxioAdmin;
      });
  }

  prepAccountState(
    adminAccount: AdminAccount & GetAccountResponse,
    token: string = null
  ): AccountState {
    // Save JWT sent from server in localstorage
    if (token) this.jwtService.saveToken(token);

    this.permissionService.setIsOxioAdmin();

    this.dispatchBrand(this.isOxioAdmin, adminAccount);

    return {
      isOxioAdmin: this.isOxioAdmin,
      adminAccount,
      permissions: adminAccount.tokenPermissions ?? adminAccount.permissions,
    };
  }

  logout() {
    this.purgeAuth();
  }

  /**
   * Purge the admin account and authentication
   * @method purgeAuth
   */
  purgeAuth(): void {
    // Remove JWT from localstorage
    this.jwtService.destroyToken();
    this.oxioAdminBrandService.destroyBrandUuid();
    this.store.dispatch(purge());
    // Remove JWT oxio sisense cookie
    this.cookieService.destroyCookie(DefaultConst.SISENSE_JWT_COOKIE_NAME);
  }

  /**
   * Attempt to authenticate the admin account
   * @method attemptAuth
   * @param {any} credentials the current admin account
   * @returns {Observable<AdminAccount>} the admin account observable
   */
  attemptAuth(credentials: CredentialsLogin): Observable<AuthResponse> {
    return this.apiService.post('/v1/accounts/login/creds', credentials);
  }

  /**
   * Verify JWT in local storage with server & load admin account's info
   * permissions: {mode: "+r", res: "accounts.*", dom:"[accountId,$brandId]", tokenDom:"[accountUuid,$brandUuid]"}
   * @param getAccountParams
   * @returns
   */
  getAccount(
    getAccountParams?: GetAccountParams
  ): Observable<GetAccountResponse> {
    if (getAccountParams) {
      let params = new HttpParams();

      Object.keys(getAccountParams).forEach((param) => {
        params = params.append(param, getAccountParams[param]);
      });

      return this.apiService.get(
        `/v2-internal/account/${getAccountParams.accountId}`,
        params
      );
    }

    return this.apiService.get('/v2-internal/account');
  }

  updateAccountV2(
    updateAccountV2Params: UpdateAccountV2Params
  ): Observable<UpdateAccountV2Response> {
    let params = this.apiService.queryParams(updateAccountV2Params);

    return this.apiService.put(
      `/v2-internal/account/${updateAccountV2Params.accountId}`,
      updateAccountV2Params
    );
  }

  /**
   * Update Account Status
   * permissions: {mode: "+u", res: "accounts.*", dom:"[$accountId,brandId]", tokenDom: "[accountUuid,brandUuid]"}
   * @param updateAccountStatusV2Params
   * @returns
   */
  updateAccountStatus(
    updateAccountStatusV2Params: UpdateAccountStatusV2Params
  ): Observable<UpdateAccountStatusV2Response> {
    return this.apiService.post(
      `/v2-internal/accounts/status/${updateAccountStatusV2Params.accountId}`,
      updateAccountStatusV2Params
    );
  }

  /**
   * Create role
   * @param role
   * permissions: {mode: "+c", res: "role.*", dom:"[$organizationUuid,$brandUuid,$groupUuid]", tokenDom:"[organization_uuid,brand_uuid,group_uuid]"}
   */
  createRole(role: Role): Observable<CreateRoleResponse> {
    return this.apiService.post('/v2-internal/roles', role);
  }

  /**
   * List brand roles
   * @param listRolesParams
   * permissions: {mode: "+r", res: "role.*", dom:"[$organizationUuid,$brandUuid,$groupUuid]", tokenDom:"[organization_uuid,brand_uuid,group_uuid]"}
   */
  listRoles(listRolesParams: ListRolesParams): Observable<ListRolesResponse> {
    let params = new HttpParams();

    Object.keys(listRolesParams).forEach((param) => {
      params = params.append(param, listRolesParams[param]);
    });

    return this.apiService.get(`/v2-internal/roles`, params);
  }

  /**
   * permissions: {mode: "+u", res: "role.*", dom:"[$organizationUuid,$brandUuid,$groupUuid]", tokenDom:"[organizationUuid,brandUuid,groupUuid]"}
   * @param updateRoleParams
   * @returns
   */
  updateRolePermissions(
    updateRolePermissionsParams: UpdateRoleParams
  ): Observable<UpdateRolePermissionsResponse> {
    return this.apiService.put(
      `/v2-internal/roles/${updateRolePermissionsParams.roleId}`,
      updateRolePermissionsParams
    );
  }

  /**
   * Delete role
   * @param deleteRoleParams
   * permissions: {mode: "+d", res: "role.*", dom:"[$organizationUuid,$brandUuid,$groupUuid]", tokenDom:"[organization_uuid,brand_uuid,group_uuid]"}
   */
  deleteRole(
    deleteRoleParams: DeleteRoleParams
  ): Observable<DeleteRoleResponse> {
    return this.apiService.delete(
      `/v2-internal/roles/${deleteRoleParams.roleUuid}`
    );
  }

  /**
   * permissions: {mode: "+c", res: "scopes.*", dom:"[$organizationUuid,$brandUuid,$groupUuid]", tokenDom:"[organizationUuid,brandUuid,groupUuid]"}
   * @param createScopeParams
   * @returns
   */
  createScope(
    createScopeParams: CreateScopeParams
  ): Observable<CreateScopeResponse> {
    return this.apiService.post('/v2-internal/scopes', createScopeParams);
  }

  /**
   * permissions: {mode: "+r", res: "scopes.*", dom:"[$organizationUuid,$brandUuid,$groupUuid]", tokenDom:"[organizationUuid,brandUuid,groupUuid]"}
   * @param listScopesParams
   * @returns
   */
  listScopes(
    listScopesParams: ListScopesParams
  ): Observable<ListScopesResponse> {
    const params: HttpParams = this.apiService.queryParams(listScopesParams);
    return this.apiService.get('/v2-internal/scopes', params);
  }

  /**
   * permissions: {mode: "+u", res: "scopes.*"}
   * @param updateScopeParams
   * @returns
   */
  updateScope(
    updateScopeParams: UpdateScopeParams
  ): Observable<UpdateScopeResponse> {
    return this.apiService.put(
      `/v2-internal/scopes/${updateScopeParams.scopeId}`,
      updateScopeParams
    );
  }

  /**
   * permissions: {mode: "+d", res: "scopes.*", dom:"[$organizationId,$brandId,$groupId]", tokenDom:"[organizationUuid,brandUuid,groupUuid]"}
   * @param deleteScopeParams
   * @returns
   */
  deleteScope(
    deleteScopeParams: DeleteScopeParams
  ): Observable<DeleteScopeResponse> {
    let params = new HttpParams();

    Object.keys(deleteScopeParams).forEach((param) => {
      params = params.append(param, deleteScopeParams[param]);
    });

    return this.apiService.delete(
      `/v2-internal/scopes/${deleteScopeParams.scopeId}`,
      params
    );
  }

  syncAccounts(
    syncAccountsParams: SyncAccountsParams
  ): Observable<SyncAccountsResponse> {
    return this.apiService.post(
      '/v2-internal/accounts/sync',
      syncAccountsParams
    );
  }
}
